import * as React from 'react';

import { cn } from '@/lib/cn';

/**
 * Input component with error, disabled, and focus states.
 * 44px minimum height for touch targets (ADR-017).
 * Supports aria-invalid for accessibility.
 * Tier 1 component - used for amount entry, search, settings.
 */
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Error message to display below the input */
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, ...props }, ref): React.ReactElement => {
    return (
      <div className="w-full">
        <input
          type={type}
          className={cn(
            'flex h-12 w-full rounded-xl border border-input bg-card px-4 py-2 text-sm tracking-tight',
            'transition-colors duration-fast ease-default',
            'file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus-visible:ring-destructive',
            className,
          )}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${props.id}-error` : undefined}
          {...props}
        />
        {error ? (
          <p id={`${props.id}-error`} className="mt-1.5 text-xs text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
export type { InputProps };
